import React, { useEffect, useState } from "react";
import { GridStack } from "gridstack";
import "gridstack/dist/gridstack.min.css";
import "../style/AnalisisPage.css";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";


import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    Line,
    LineChart,
    CartesianGrid,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    ScatterChart,
    Scatter,
    ResponsiveContainer,
    ComposedChart,
    PieChart,
    Pie,
    Cell,
} from "recharts";


import {
    Activity,
    Zap,
    FileText,
    Download,
    RefreshCw,
    TrendingUp,
    BarChart3,
    Filter,
    AlertTriangle,
    TrendingDown,
    MinusCircle,
} from "lucide-react";

const Analicis = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [terrenos, setTerrenos] = useState([]);
    const [sensores, setSensores] = useState([]);
    const [terrenoSeleccionado1, setTerrenoSeleccionado1] = useState("");
    const [terrenoSeleccionado2, setTerrenoSeleccionado2] = useState("");
    const [sensorSeleccionado1, setSensorSeleccionado1] = useState("");
    const [sensorSeleccionado2, setSensorSeleccionado2] = useState("");
    const [datosAnalisis, setDatosAnalisis] = useState(null);
    const [reporteIA, setReporteIA] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [generandoReporte, setGenerandoReporte] = useState(false);

    const COLORES = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

    useEffect(() => {
        cargarTerrenos();
        cargarSensores();
    }, []);

    useEffect(() => {
        if (datosAnalisis) {
            setTimeout(() => {
                const gridElement = document.querySelector('.grid-stack');
                if (gridElement && !gridElement.gridstack) {
                    GridStack.init({
                        column: 12,
                        cellHeight: 100,
                        margin: 10,
                        float: false,
                        resizable: { handles: "e, se, s, sw, w", autoHide: false },
                        draggable: { handle: ".leopardo-titulo", scroll: true },
                        disableOneColumnMode: true,
                        animate: false,
                        staticGrid: false,
                    });
                }
            }, 100);
        }
    }, [datosAnalisis, reporteIA]);

    const cargarTerrenos = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            const res = await fetch("https://agro-nexus-prod.vercel.app/api/terrenos", {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setTerrenos(data.terrenos);
            }
        } catch (error) {
            console.error("Error cargando terrenos:", error);
        }
    };

    const cargarSensores = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        try {
            const res = await fetch("https://agro-nexus-prod.vercel.app/api/sensores", {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setSensores(data.sensores);
            }
        } catch (error) {
            console.error("Error cargando sensores:", error);
        }
    };

    const realizarAnalisis = async () => {
        if (!terrenoSeleccionado1 && !sensorSeleccionado1) {
            alert("Selecciona al menos un terreno o sensor para analizar");
            return;
        }

        setCargando(true);
        const token = localStorage.getItem("token");

        try {
            const datosSensores = await obtenerDatosSensores(token);
            const analisis = calcularAnalisis(datosSensores);
            setDatosAnalisis(analisis);
        } catch (error) {
            console.error("Error en análisis:", error);
            alert("Error al realizar el análisis");
        } finally {
            setCargando(false);
        }
    };

    const obtenerDatosSensores = async (token) => {
        const sensoresAConsultar = [];

        if (sensorSeleccionado1) sensoresAConsultar.push(sensorSeleccionado1);
        if (sensorSeleccionado2) sensoresAConsultar.push(sensorSeleccionado2);

        if (terrenoSeleccionado1) {
            const sensoresTerreno1 = sensores.filter(s => s.terreno_id == terrenoSeleccionado1);
            sensoresAConsultar.push(...sensoresTerreno1.map(s => s.id));
        }
        if (terrenoSeleccionado2) {
            const sensoresTerreno2 = sensores.filter(s => s.terreno_id == terrenoSeleccionado2);
            sensoresAConsultar.push(...sensoresTerreno2.map(s => s.id));
        }

        const datosPorSensor = {};

        for (const sensorId of sensoresAConsultar) {
            const res = await fetch(
                `https://agro-nexus-prod.vercel.app/api/sensores/${sensorId}/datos?limite=100`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = await res.json();

            if (data.success && data.datos.length > 0) {
                const sensor = sensores.find(s => s.id == sensorId);
                datosPorSensor[sensorId] = {
                    sensor: sensor,
                    datos: data.datos.map(d => ({
                        fecha: d.fecha_hora,
                        valor: parseFloat(d.valor),
                        hora: new Date(d.fecha_hora).toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit'
                        }),
                        timestamp: new Date(d.fecha_hora).getTime()
                    }))
                };
            }
        }

        return datosPorSensor;
    };

    const calcularAnalisis = (datosSensores) => {
        const analisis = {
            promedios: {},
            maximos: {},
            minimos: {},
            desviaciones: {},
            tendencias: {},
            comparacion: [],
            alertas: [],
            seriesTemporales: [],
            correlaciones: [],
            distribucion: [],
            datosRadar: [],
            evolucionComparativa: [],
            estadoSalud: [],
            variabilidad: []
        };

        const sensoresIds = Object.keys(datosSensores);

        sensoresIds.forEach((sensorId, index) => {
            const { sensor, datos } = datosSensores[sensorId];
            const valores = datos.map(d => d.valor);

            const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
            const maximo = Math.max(...valores);
            const minimo = Math.min(...valores);

            const varianza = valores.reduce((sum, val) => sum + Math.pow(val - promedio, 2), 0) / valores.length;
            const desviacion = Math.sqrt(varianza);

            analisis.promedios[sensorId] = promedio.toFixed(2);
            analisis.maximos[sensorId] = maximo.toFixed(2);
            analisis.minimos[sensorId] = minimo.toFixed(2);
            analisis.desviaciones[sensorId] = desviacion.toFixed(2);

            const primeraMitad = valores.slice(0, Math.floor(valores.length / 2));
            const segundaMitad = valores.slice(Math.floor(valores.length / 2));
            const promPrimera = primeraMitad.reduce((a, b) => a + b, 0) / primeraMitad.length;
            const promSegunda = segundaMitad.reduce((a, b) => a + b, 0) / segundaMitad.length;

            const tendencia = promSegunda > promPrimera ? "creciente" :
                promSegunda < promPrimera ? "decreciente" : "estable";
            const cambio = ((promSegunda - promPrimera) / promPrimera * 100).toFixed(1);

            analisis.tendencias[sensorId] = { tipo: tendencia, cambio: cambio };

            if (sensor.valor_minimo && promedio < sensor.valor_minimo) {
                analisis.alertas.push({
                    sensor: sensor.nombre,
                    tipo: "bajo",
                    severidad: "alta",
                    mensaje: `${sensor.nombre}: Promedio (${promedio.toFixed(2)}) por debajo del mínimo (${sensor.valor_minimo})`
                });
            }
            if (sensor.valor_maximo && promedio > sensor.valor_maximo) {
                analisis.alertas.push({
                    sensor: sensor.nombre,
                    tipo: "alto",
                    severidad: "alta",
                    mensaje: `${sensor.nombre}: Promedio (${promedio.toFixed(2)}) por encima del máximo (${sensor.valor_maximo})`
                });
            }

            let estadoSalud = "óptimo";
            let porcentajeSalud = 100;

            if (sensor.valor_minimo && sensor.valor_maximo) {
                const rango = sensor.valor_maximo - sensor.valor_minimo;
                const centro = (sensor.valor_maximo + sensor.valor_minimo) / 2;
                const distancia = Math.abs(promedio - centro);
                porcentajeSalud = Math.max(0, 100 - (distancia / rango * 200));

                if (porcentajeSalud < 50) estadoSalud = "crítico";
                else if (porcentajeSalud < 75) estadoSalud = "regular";
                else if (porcentajeSalud < 90) estadoSalud = "bueno";
            }

            analisis.estadoSalud.push({
                sensor: sensor.nombre,
                estado: estadoSalud,
                porcentaje: porcentajeSalud.toFixed(0),
                color: porcentajeSalud >= 90 ? '#10b981' :
                    porcentajeSalud >= 75 ? '#6366f1' :
                        porcentajeSalud >= 50 ? '#f59e0b' : '#ef4444'
            });

            analisis.seriesTemporales.push({
                nombre: sensor.nombre,
                tipo: sensor.tipo_sensor,
                datos: datos.slice(-30).map(d => ({
                    hora: d.hora,
                    valor: d.valor,
                    sensor: sensor.nombre
                }))
            });

            analisis.comparacion.push({
                sensor: sensor.nombre,
                tipo: sensor.tipo_sensor,
                promedio: parseFloat(analisis.promedios[sensorId]),
                maximo: parseFloat(analisis.maximos[sensorId]),
                minimo: parseFloat(analisis.minimos[sensorId]),
                desviacion: parseFloat(analisis.desviaciones[sensorId])
            });

            if (sensor.valor_minimo && sensor.valor_maximo) {
                analisis.datosRadar.push({
                    factor: sensor.nombre,
                    valor: ((promedio - sensor.valor_minimo) / (sensor.valor_maximo - sensor.valor_minimo) * 100).toFixed(0),
                    optimo: 100
                });
            }

            analisis.distribucion.push({
                name: sensor.nombre.substring(0, 10),
                valor: promedio,
                color: COLORES[index % COLORES.length]
            });

            analisis.variabilidad.push({
                sensor: sensor.nombre,
                min: minimo,
                promedio: promedio,
                max: maximo,
                desviacion: desviacion
            });
        });

        if (sensoresIds.length >= 2) {
            const maxLength = Math.min(
                ...sensoresIds.map(id => datosSensores[id].datos.length)
            );

            analisis.evolucionComparativa = Array.from({ length: Math.min(maxLength, 20) }, (_, i) => {
                const punto = { hora: i };
                sensoresIds.forEach(sensorId => {
                    const sensor = datosSensores[sensorId].sensor;
                    const dato = datosSensores[sensorId].datos[i];
                    punto[sensor.nombre] = dato ? dato.valor : null;
                    if (i === 0) punto.hora = dato?.hora || i;
                });
                return punto;
            });

            if (sensoresIds.length === 2) {
                const [id1, id2] = sensoresIds;
                const datos1 = datosSensores[id1].datos.slice(0, 20);
                const datos2 = datosSensores[id2].datos.slice(0, 20);

                analisis.correlaciones = datos1.map((d1, i) => ({
                    x: d1.valor,
                    y: datos2[i]?.valor || 0,
                    hora: d1.hora
                }));
            }
        }

        return analisis;
    };

    const generarReporteConIA = async () => {
        if (!datosAnalisis) {
            alert("Primero realiza un análisis");
            return;
        }

        setGenerandoReporte(true);

        try {
            const sensoresAnalizado = sensores.filter(s =>
                s.id == sensorSeleccionado1 || s.id == sensorSeleccionado2 ||
                (terrenoSeleccionado1 && s.terreno_id == terrenoSeleccionado1) ||
                (terrenoSeleccionado2 && s.terreno_id == terrenoSeleccionado2)
            );

            const prompt = `
Eres un agrónomo experto especializado en agricultura de precisión. Analiza los siguientes datos de sensores agrícolas:

SENSORES ANALIZADOS:
${sensoresAnalizado.map(s => `- ${s.nombre} (${s.tipo_sensor}): Rango óptimo ${s.valor_minimo || 'N/A'} - ${s.valor_maximo || 'N/A'}`).join('\n')}

ESTADÍSTICAS:
${JSON.stringify(datosAnalisis.comparacion, null, 2)}

ALERTAS DETECTADAS:
${datosAnalisis.alertas.map(a => `- ${a.mensaje}`).join('\n') || 'Ninguna'}

TENDENCIAS:
${Object.entries(datosAnalisis.tendencias).map(([id, t]) => {
                const sensor = sensores.find(s => s.id == id);
                return `- ${sensor?.nombre}: ${t.tipo} (${t.cambio}%)`;
            }).join('\n')}

ESTADO DE SALUD:
${datosAnalisis.estadoSalud.map(e => `- ${e.sensor}: ${e.estado} (${e.porcentaje}%)`).join('\n')}

Proporciona un análisis profesional en formato JSON:
{
    "resumen_ejecutivo": "Breve resumen de la situación actual",
    "condiciones_actuales": "Descripción detallada de las condiciones",
    "analisis_detallado": "Análisis profundo de los datos y patrones",
    "alertas": ["Lista de alertas críticas con explicación"],
    "recomendaciones": ["Lista de recomendaciones específicas y accionables"],
    "acciones_inmediatas": ["Acciones urgentes a tomar en las próximas 24-48 horas"],
    "pronostico": "Proyección a corto plazo basada en las tendencias",
    "optimizaciones": ["Sugerencias para optimizar el uso de recursos"]
}

Sé específico, técnico pero comprensible, y enfócate en soluciones prácticas.
`;

            const response = await fetch("https://api.openai.com/v1/chat/completions", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": "Bearer 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"
                },
                body: JSON.stringify({
                    model: "gpt-3.5-turbo",
                    messages: [
                        {
                            role: "system",
                            content: "Eres un agrónomo experto que analiza datos de sensores agrícolas y proporciona recomendaciones profesionales basadas en agricultura de precisión."
                        },
                        {
                            role: "user",
                            content: prompt
                        }
                    ],
                    temperature: 0.7,
                    max_tokens: 2500
                })
            });

            const data = await response.json();

            if (data.choices && data.choices[0]) {
                const contenido = data.choices[0].message.content;

                try {
                    const reporteJSON = JSON.parse(contenido);
                    setReporteIA(reporteJSON);
                } catch {
                    const jsonMatch = contenido.match(/\{[\s\S]*\}/);
                    if (jsonMatch) {
                        try {
                            const reporteJSON = JSON.parse(jsonMatch[0]);
                            setReporteIA(reporteJSON);
                        } catch {
                            setReporteIA({
                                resumen_ejecutivo: contenido,
                                condiciones_actuales: "",
                                analisis_detallado: "",
                                alertas: [],
                                recomendaciones: [],
                                acciones_inmediatas: [],
                                pronostico: "",
                                optimizaciones: []
                            });
                        }
                    }
                }
            }

        } catch (error) {
            console.error("Error generando reporte con IA:", error);
            alert("Error al generar el reporte con IA. Verifica tu conexión y el token de API.");
        } finally {
            setGenerandoReporte(false);
        }
    };

    const exportarPDF = async () => {
        const doc = new jsPDF();
        const fecha = new Date().toLocaleDateString('es-ES');
        let yPos = 20;

        doc.setFillColor(99, 102, 241);
        doc.rect(0, 0, 210, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont(undefined, 'bold');
        doc.text("REPORTE DE ANÁLISIS", 105, 20, { align: "center" });
        doc.text("AGRÍCOLA", 105, 30, { align: "center" });

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Fecha de generación: ${fecha}`, 105, 50, { align: "center" });

        doc.setTextColor(0, 0, 0);
        yPos = 70;

        if (reporteIA?.resumen_ejecutivo) {
            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(99, 102, 241);
            doc.text("📊 RESUMEN EJECUTIVO", 20, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
            const lineas = doc.splitTextToSize(reporteIA.resumen_ejecutivo, 170);
            doc.text(lineas, 20, yPos);
            yPos += (lineas.length * 5) + 15;
        }

        doc.addPage();
        yPos = 20;

        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(99, 102, 241);
        doc.text("📈 ESTADÍSTICAS DETALLADAS", 20, yPos);
        yPos += 10;

        if (datosAnalisis) {
            const tableData = datosAnalisis.comparacion.map((c, i) => {
                const tendencia = Object.values(datosAnalisis.tendencias)[i];
                const salud = datosAnalisis.estadoSalud[i];
                return [
                    c.sensor,
                    c.tipo,
                    c.promedio,
                    c.minimo,
                    c.maximo,
                    c.desviacion,
                    `${tendencia?.tipo || 'N/A'} (${tendencia?.cambio || '0'}%)`,
                    `${salud?.estado || 'N/A'} (${salud?.porcentaje || '0'}%)`
                ];
            });

            doc.autoTable({
                startY: yPos,
                head: [['Sensor', 'Tipo', 'Prom.', 'Mín.', 'Máx.', 'Desv.', 'Tendencia', 'Salud']],
                body: tableData,
                theme: 'grid',
                headStyles: { fillColor: [99, 102, 241] },
                styles: { fontSize: 8 },
                columnStyles: {
                    0: { cellWidth: 30 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 18 },
                    3: { cellWidth: 18 },
                    4: { cellWidth: 18 },
                    5: { cellWidth: 18 },
                    6: { cellWidth: 30 },
                    7: { cellWidth: 25 }
                }
            });

            yPos = doc.lastAutoTable.finalY + 15;
        }

        if (datosAnalisis?.alertas && datosAnalisis.alertas.length > 0) {
            if (yPos > 200) {
                doc.addPage();
                yPos = 20;
            }

            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(239, 68, 68);
            doc.text("⚠️ ALERTAS CRÍTICAS", 20, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);

            datosAnalisis.alertas.forEach((alerta, i) => {
                if (yPos > 270) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setDrawColor(239, 68, 68);
                doc.setFillColor(254, 242, 242);
                doc.roundedRect(20, yPos - 5, 170, 10, 2, 2, 'FD');
                doc.text(`${i + 1}. ${alerta.mensaje}`, 25, yPos);
                yPos += 15;
            });
        }

        if (reporteIA?.recomendaciones && reporteIA.recomendaciones.length > 0) {
            doc.addPage();
            yPos = 20;

            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(16, 185, 129);
            doc.text("💡 RECOMENDACIONES", 20, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);

            reporteIA.recomendaciones.forEach((rec, i) => {
                if (yPos > 270) {
                    doc.addPage();
                    yPos = 20;
                }
                const texto = doc.splitTextToSize(`${i + 1}. ${rec}`, 165);
                doc.text(texto, 25, yPos);
                yPos += (texto.length * 5) + 5;
            });
        }

        if (reporteIA?.acciones_inmediatas && reporteIA.acciones_inmediatas.length > 0) {
            if (yPos > 200) {
                doc.addPage();
                yPos = 20;
            } else {
                yPos += 10;
            }

            doc.setFontSize(16);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(245, 158, 11);
            doc.text("🎯 ACCIONES INMEDIATAS", 20, yPos);
            yPos += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);

            reporteIA.acciones_inmediatas.forEach((accion, i) => {
                if (yPos > 270) {
                    doc.addPage();
                    yPos = 20;
                }
                doc.setDrawColor(245, 158, 11);
                doc.setFillColor(255, 251, 235);
                doc.roundedRect(20, yPos - 5, 170, 10, 2, 2, 'FD');
                const texto = doc.splitTextToSize(`${i + 1}. ${accion}`, 160);
                doc.text(texto, 25, yPos);
                yPos += (texto.length * 5) + 8;
            });
        }

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text(`Página ${i} de ${pageCount}`, 105, 290, { align: 'center' });
            doc.text('Generado por AgroNexus - Sistema de Monitoreo Agrícola', 105, 285, { align: 'center' });
        }

        doc.save(`reporte_analisis_${fecha.replace(/\//g, '-')}.pdf`);
    };

    const exportarExcel = () => {
        if (!datosAnalisis) return;

        const wb = XLSX.utils.book_new();
        const fecha = new Date().toLocaleDateString('es-ES');

        const portadaData = [
            ["REPORTE DE ANÁLISIS AGRÍCOLA"],
            [""],
            ["Fecha:", fecha],
            ["Sistema:", "AgroNexus"],
            [""],
            ["RESUMEN DEL ANÁLISIS"],
            ["Total de sensores analizados:", datosAnalisis.comparacion.length],
            ["Alertas detectadas:", datosAnalisis.alertas.length],
            [""],
        ];

        const ws1 = XLSX.utils.aoa_to_sheet(portadaData);
        ws1['!cols'] = [{ wch: 30 }, { wch: 40 }];
        XLSX.utils.book_append_sheet(wb, ws1, "Portada");

        const estadisticasData = [
            ["ESTADÍSTICAS POR SENSOR"],
            [""],
            ["Sensor", "Tipo", "Promedio", "Mínimo", "Máximo", "Desviación Std", "Tendencia", "% Cambio", "Estado Salud", "% Salud"],
            ...datosAnalisis.comparacion.map((c, i) => {
                const tendencia = Object.values(datosAnalisis.tendencias)[i];
                const salud = datosAnalisis.estadoSalud[i];
                return [
                    c.sensor,
                    c.tipo,
                    c.promedio,
                    c.minimo,
                    c.maximo,
                    c.desviacion,
                    tendencia?.tipo || 'N/A',
                    tendencia?.cambio || '0',
                    salud?.estado || 'N/A',
                    salud?.porcentaje || '0'
                ];
            })
        ];

        const ws2 = XLSX.utils.aoa_to_sheet(estadisticasData);
        ws2['!cols'] = Array(10).fill({ wch: 15 });
        XLSX.utils.book_append_sheet(wb, ws2, "Estadísticas");

        if (datosAnalisis.alertas.length > 0) {
            const alertasData = [
                ["ALERTAS DETECTADAS"],
                [""],
                ["Sensor", "Tipo", "Severidad", "Mensaje"],
                ...datosAnalisis.alertas.map(a => [
                    a.sensor,
                    a.tipo,
                    a.severidad,
                    a.mensaje
                ])
            ];

            const ws3 = XLSX.utils.aoa_to_sheet(alertasData);
            ws3['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 60 }];
            XLSX.utils.book_append_sheet(wb, ws3, "Alertas");
        }

        if (reporteIA) {
            const recomendacionesData = [
                ["ANÁLISIS CON INTELIGENCIA ARTIFICIAL"],
                [""],
                ["RESUMEN EJECUTIVO"],
                [reporteIA.resumen_ejecutivo || ""],
                [""],
                ["CONDICIONES ACTUALES"],
                [reporteIA.condiciones_actuales || ""],
                [""],
                ["ANÁLISIS DETALLADO"],
                [reporteIA.analisis_detallado || ""],
                [""],
                ["RECOMENDACIONES"],
                ...(reporteIA.recomendaciones || []).map((r, i) => [`${i + 1}. ${r}`]),
                [""],
                ["ACCIONES INMEDIATAS"],
                ...(reporteIA.acciones_inmediatas || []).map((a, i) => [`${i + 1}. ${a}`]),
                [""],
                ["PRONÓSTICO"],
                [reporteIA.pronostico || ""],
                [""],
                ["OPTIMIZACIONES SUGERIDAS"],
                ...(reporteIA.optimizaciones || []).map((o, i) => [`${i + 1}. ${o}`])
            ];

            const ws4 = XLSX.utils.aoa_to_sheet(recomendacionesData);
            ws4['!cols'] = [{ wch: 100 }];
            XLSX.utils.book_append_sheet(wb, ws4, "Recomendaciones IA");
        }

        const historicoData = [
            ["DATOS HISTÓRICOS"],
            [""],
            ["Sensor", "Hora", "Valor"]
        ];

        datosAnalisis.seriesTemporales.forEach(serie => {
            serie.datos.forEach(d => {
                historicoData.push([serie.nombre, d.hora, d.valor]);
            });
        });

        const ws5 = XLSX.utils.aoa_to_sheet(historicoData);
        ws5['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, ws5, "Datos Históricos");

        XLSX.writeFile(wb, `reporte_analisis_${fecha.replace(/\//g, '-')}.xlsx`);
    };

    const renderIconoTendencia = (tendencia) => {
        if (tendencia?.tipo === "creciente") {
            return <TrendingUp size={16} color="#10b981" />;
        } else if (tendencia?.tipo === "decreciente") {
            return <TrendingDown size={16} color="#ef4444" />;
        }
        return <MinusCircle size={16} color="#6b7280" />;
    };

    return (
        <div className="elefante-root elefante-oscuro">
            <div className="jirafa-sidebar">
                <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
            </div>
            <div className="tigre-main">
                <Header isOpen={isOpen} setIsOpen={setIsOpen} />
                <div className="leon-content">

                    <div className="cocodrilo-card cocodrilo-dark" style={{ marginBottom: "20px" }}>
                        <h2 className="leopardo-titulo">
                            <BarChart3 size={24} />
                            Análisis y Comparación de Datos
                        </h2>

                        <div className="aguila-filtros">
                            <div className="oso-grupo">
                                <label className="panda-label">
                                    <Filter size={16} /> Terreno 1
                                </label>
                                <select
                                    value={terrenoSeleccionado1}
                                    onChange={(e) => setTerrenoSeleccionado1(e.target.value)}
                                    className="lobo-select"
                                >
                                    <option value="">Seleccionar terreno</option>
                                    {terrenos.map(t => (
                                        <option key={t.id} value={t.id}>{t.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="oso-grupo">
                                <label className="panda-label">Terreno 2 (comparar)</label>
                                <select
                                    value={terrenoSeleccionado2}
                                    onChange={(e) => setTerrenoSeleccionado2(e.target.value)}
                                    className="lobo-select"
                                >
                                    <option value="">Seleccionar terreno</option>
                                    {terrenos.map(t => (
                                        <option key={t.id} value={t.id}>{t.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="oso-grupo">
                                <label className="panda-label">Sensor 1</label>
                                <select
                                    value={sensorSeleccionado1}
                                    onChange={(e) => setSensorSeleccionado1(e.target.value)}
                                    className="lobo-select"
                                >
                                    <option value="">Seleccionar sensor</option>
                                    {sensores.map(s => (
                                        <option key={s.id} value={s.id}>
                                            {s.nombre} - {s.tipo_sensor}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="oso-grupo">
                                <label className="panda-label">Sensor 2 (comparar)</label>
                                <select
                                    value={sensorSeleccionado2}
                                    onChange={(e) => setSensorSeleccionado2(e.target.value)}
                                    className="lobo-select"
                                >
                                    <option value="">Seleccionar sensor</option>
                                    {sensores.map(s => (
                                        <option key={s.id} value={s.id}>
                                            {s.nombre} - {s.tipo_sensor}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                className="zorro-boton zorro-primario"
                                onClick={realizarAnalisis}
                                disabled={cargando}
                            >
                                {cargando ? (
                                    <><RefreshCw size={16} className="girando" /> Analizando...</>
                                ) : (
                                    <><Activity size={16} /> Realizar Análisis</>
                                )}
                            </button>
                        </div>
                    </div>

                    {!datosAnalisis && !cargando && (
                        <div className="delfin-vacio">
                            <BarChart3 size={60} style={{ opacity: 0.3 }} />
                            <h3>Selecciona terrenos o sensores para comenzar el análisis</h3>
                            <p>Compara datos entre diferentes terrenos o sensores para obtener insights valiosos</p>
                        </div>
                    )}

                    {cargando && (
                        <div className="delfin-vacio">
                            <RefreshCw size={60} className="girando" style={{ color: '#6366f1' }} />
                            <h3>Analizando datos...</h3>
                            <p>Procesando información de los sensores seleccionados</p>
                        </div>
                    )}


                    {datosAnalisis && (() => {

                        const numStats = datosAnalisis.comparacion.length;
                        const statsRows = Math.ceil(numStats / 4);
                        let yPos = statsRows * 3;

                        return (
                            <div className="grid-stack" key={`grid-${Date.now()}`}>

                                {datosAnalisis.comparacion.map((comp, index) => {
                                    const tendencia = Object.values(datosAnalisis.tendencias)[index];
                                    const salud = datosAnalisis.estadoSalud[index];
                                    const col = index % 4;
                                    const row = Math.floor(index / 4);

                                    return (
                                        <div
                                            key={`stat-${index}`}
                                            className="grid-stack-item"
                                            gs-w="3"
                                            gs-h="3"
                                            gs-x={col * 3}
                                            gs-y={row * 3}
                                        >
                                            <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                                <h4 className="leopardo-titulo">{comp.sensor}</h4>
                                                <div className="ballena-valor">{comp.promedio}</div>
                                                <div className="foca-subtexto">{comp.tipo}</div>
                                                <div className="pulpo-rango">
                                                    Min: {comp.minimo} | Max: {comp.maximo}
                                                </div>
                                                <div style={{
                                                    marginTop: '12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    fontSize: '0.85rem',
                                                    color: '#9ca3af'
                                                }}>
                                                    {renderIconoTendencia(tendencia)}
                                                    <span>{tendencia?.tipo} ({tendencia?.cambio}%)</span>
                                                </div>
                                                <div style={{
                                                    marginTop: '8px',
                                                    padding: '8px',
                                                    borderRadius: '6px',
                                                    backgroundColor: `${salud?.color}20`,
                                                    border: `1px solid ${salud?.color}`,
                                                    textAlign: 'center',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: salud?.color
                                                }}>
                                                    Salud: {salud?.estado} ({salud?.porcentaje}%)
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}


                                <div className="grid-stack-item" gs-w="6" gs-h="5" gs-x="0" gs-y={yPos} id="grafico-comparacion">
                                    <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                        <h4 className="leopardo-titulo">
                                            <BarChart3 size={18} />
                                            Comparación Estadística
                                        </h4>
                                        <ResponsiveContainer width="100%" height="85%">
                                            <BarChart data={datosAnalisis.comparacion}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#3d4463" />
                                                <XAxis
                                                    dataKey="sensor"
                                                    stroke="#9ca3af"
                                                    style={{ fontSize: '11px' }}
                                                />
                                                <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: '#252b48',
                                                        border: '1px solid #3d4463',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                <Bar dataKey="promedio" fill="#6366f1" name="Promedio" />
                                                <Bar dataKey="maximo" fill="#10b981" name="Máximo" />
                                                <Bar dataKey="minimo" fill="#f59e0b" name="Mínimo" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>


                                <div className="grid-stack-item" gs-w="6" gs-h="5" gs-x="6" gs-y={yPos} id="grafico-distribucion">
                                    <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                        <h4 className="leopardo-titulo">
                                            <Activity size={18} />
                                            Distribución de Promedios
                                        </h4>
                                        <ResponsiveContainer width="100%" height="85%">
                                            <PieChart>
                                                <Pie
                                                    data={datosAnalisis.distribucion}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    dataKey="valor"
                                                >
                                                    {datosAnalisis.distribucion.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{
                                                        background: '#252b48',
                                                        border: '1px solid #3d4463',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                <div className="grid-stack-item" gs-w="12" gs-h="5" gs-x="0" gs-y={yPos + 5} id="grafico-temporal">
                                    <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                        <h4 className="leopardo-titulo">
                                            <TrendingUp size={18} />
                                            Evolución Temporal
                                        </h4>
                                        <ResponsiveContainer width="100%" height="85%">
                                            <ComposedChart data={datosAnalisis.evolucionComparativa.length > 0 ?
                                                datosAnalisis.evolucionComparativa :
                                                datosAnalisis.seriesTemporales[0]?.datos || []}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#3d4463" />
                                                <XAxis
                                                    dataKey="hora"
                                                    stroke="#9ca3af"
                                                    style={{ fontSize: '11px' }}
                                                />
                                                <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: '#252b48',
                                                        border: '1px solid #3d4463',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                {datosAnalisis.seriesTemporales.map((serie, i) => (
                                                    <Line
                                                        key={i}
                                                        type="monotone"
                                                        dataKey={serie.nombre}
                                                        stroke={COLORES[i % COLORES.length]}
                                                        name={serie.nombre}
                                                        strokeWidth={2}
                                                        dot={{ r: 3 }}
                                                    />
                                                ))}
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {datosAnalisis.datosRadar.length > 0 && (
                                    <div className="grid-stack-item" gs-w="6" gs-h="5" gs-x="0" gs-y={yPos + 10} id="grafico-radar">
                                        <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                            <h4 className="leopardo-titulo">
                                                <Zap size={18} />
                                                Condiciones vs Óptimas
                                            </h4>
                                            <ResponsiveContainer width="100%" height="85%">
                                                <RadarChart data={datosAnalisis.datosRadar}>
                                                    <PolarGrid stroke="#3d4463" />
                                                    <PolarAngleAxis
                                                        dataKey="factor"
                                                        stroke="#9ca3af"
                                                        style={{ fontSize: '11px' }}
                                                    />
                                                    <PolarRadiusAxis
                                                        angle={90}
                                                        domain={[0, 100]}
                                                        stroke="#9ca3af"
                                                        style={{ fontSize: '10px' }}
                                                    />
                                                    <Radar
                                                        name="Valor Actual"
                                                        dataKey="valor"
                                                        stroke="#6366f1"
                                                        fill="#6366f1"
                                                        fillOpacity={0.6}
                                                    />
                                                    <Radar
                                                        name="Valor Óptimo"
                                                        dataKey="optimo"
                                                        stroke="#10b981"
                                                        fill="#10b981"
                                                        fillOpacity={0.3}
                                                    />
                                                    <Legend />
                                                    <Tooltip
                                                        contentStyle={{
                                                            background: '#252b48',
                                                            border: '1px solid #3d4463',
                                                            borderRadius: '8px'
                                                        }}
                                                    />
                                                </RadarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {datosAnalisis.correlaciones.length > 0 && (
                                    <div className="grid-stack-item" gs-w="6" gs-h="5" gs-x="6" gs-y={yPos + 10} id="grafico-correlacion">
                                        <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                            <h4 className="leopardo-titulo">
                                                <Activity size={18} />
                                                Correlación entre Sensores
                                            </h4>
                                            <ResponsiveContainer width="100%" height="85%">
                                                <ScatterChart>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#3d4463" />
                                                    <XAxis
                                                        type="number"
                                                        dataKey="x"
                                                        name="Sensor 1"
                                                        stroke="#9ca3af"
                                                        style={{ fontSize: '11px' }}
                                                    />
                                                    <YAxis
                                                        type="number"
                                                        dataKey="y"
                                                        name="Sensor 2"
                                                        stroke="#9ca3af"
                                                        style={{ fontSize: '11px' }}
                                                    />
                                                    <Tooltip
                                                        cursor={{ strokeDasharray: "3 3" }}
                                                        contentStyle={{
                                                            background: '#252b48',
                                                            border: '1px solid #3d4463',
                                                            borderRadius: '8px'
                                                        }}
                                                    />
                                                    <Scatter
                                                        name="Relación"
                                                        data={datosAnalisis.correlaciones}
                                                        fill="#8b5cf6"
                                                        shape="circle"
                                                    />
                                                </ScatterChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                <div className="grid-stack-item" gs-w="6" gs-h="5" gs-x="0" gs-y={yPos + (datosAnalisis.datosRadar.length > 0 ? 15 : 10)} id="grafico-variabilidad">
                                    <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                        <h4 className="leopardo-titulo">
                                            <BarChart3 size={18} />
                                            Variabilidad y Rangos
                                        </h4>
                                        <ResponsiveContainer width="100%" height="85%">
                                            <BarChart data={datosAnalisis.variabilidad} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" stroke="#3d4463" />
                                                <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                                                <YAxis
                                                    type="category"
                                                    dataKey="sensor"
                                                    stroke="#9ca3af"
                                                    style={{ fontSize: '11px' }}
                                                    width={100}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: '#252b48',
                                                        border: '1px solid #3d4463',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                <Bar dataKey="min" fill="#ef4444" name="Mínimo" />
                                                <Bar dataKey="promedio" fill="#6366f1" name="Promedio" />
                                                <Bar dataKey="max" fill="#10b981" name="Máximo" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>

                                {datosAnalisis.alertas && datosAnalisis.alertas.length > 0 && (
                                    <div className="grid-stack-item" gs-w="6" gs-h="5" gs-x="6" gs-y={yPos + (datosAnalisis.datosRadar.length > 0 ? 15 : 10)} id="alertas-panel">
                                        <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                            <h4 className="leopardo-titulo">
                                                <AlertTriangle size={18} />
                                                Alertas Críticas
                                            </h4>
                                            <div className="camello-contenido">
                                                <ul className="hipopotamo-lista">
                                                    {datosAnalisis.alertas.map((alerta, i) => (
                                                        <li key={i} className="rinoceronte-item rinoceronte-alto">
                                                            <strong>{alerta.sensor}:</strong> {alerta.mensaje}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid-stack-item" gs-w="12" gs-h="3" gs-x="0" gs-y={yPos + (datosAnalisis.datosRadar.length > 0 ? 20 : 15)}>
                                    <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                        <h4 className="leopardo-titulo">
                                            <FileText size={18} />
                                            Generar Reporte Profesional
                                        </h4>
                                        <div className="cebra-botones">
                                            <button
                                                className="zorro-boton zorro-ia"
                                                onClick={generarReporteConIA}
                                                disabled={generandoReporte}
                                            >
                                                {generandoReporte ? (
                                                    <><RefreshCw size={16} className="girando" /> Generando...</>
                                                ) : (
                                                    <><Zap size={16} /> Análisis con IA</>
                                                )}
                                            </button>
                                            <button
                                                className="zorro-boton zorro-secundario"
                                                onClick={exportarPDF}
                                                disabled={!reporteIA}
                                                title={!reporteIA ? "Primero genera el análisis con IA" : "Descargar PDF"}
                                            >
                                                <Download size={16} /> Exportar PDF
                                            </button>
                                            <button
                                                className="zorro-boton zorro-secundario"
                                                onClick={exportarExcel}
                                                title="Descargar Excel con todos los datos"
                                            >
                                                <Download size={16} /> Exportar Excel
                                            </button>
                                        </div>
                                        <p style={{
                                            marginTop: '12px',
                                            fontSize: '0.85rem',
                                            color: '#9ca3af',
                                            lineHeight: '1.5'
                                        }}>
                                            💡 <strong>Tip:</strong> Genera primero el análisis con IA para obtener recomendaciones profesionales basadas en tus datos. Los reportes incluyen gráficos, estadísticas detalladas y acciones específicas.
                                        </p>
                                    </div>
                                </div>

                                {reporteIA && (
                                    <div className="grid-stack-item" gs-w="12" gs-h="6" gs-x="0" gs-y={yPos + (datosAnalisis.datosRadar.length > 0 ? 23 : 18)}>
                                        <div className="grid-stack-item-content cocodrilo-card cocodrilo-dark">
                                            <h4 className="leopardo-titulo">
                                                <Zap size={18} />
                                                Reporte de Inteligencia Artificial
                                            </h4>
                                            <div className="camello-contenido">
                                                {reporteIA.resumen_ejecutivo && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">📊 Resumen Ejecutivo</h5>
                                                        <p>{reporteIA.resumen_ejecutivo}</p>
                                                    </div>
                                                )}

                                                {reporteIA.condiciones_actuales && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">🌡️ Condiciones Actuales</h5>
                                                        <p>{reporteIA.condiciones_actuales}</p>
                                                    </div>
                                                )}

                                                {reporteIA.analisis_detallado && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">🔍 Análisis Detallado</h5>
                                                        <p>{reporteIA.analisis_detallado}</p>
                                                    </div>
                                                )}

                                                {reporteIA.alertas && reporteIA.alertas.length > 0 && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">⚠️ Alertas Críticas</h5>
                                                        <ul className="hipopotamo-lista">
                                                            {reporteIA.alertas.map((alerta, i) => (
                                                                <li key={i} className="rinoceronte-item rinoceronte-alto">
                                                                    {alerta}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {reporteIA.recomendaciones && reporteIA.recomendaciones.length > 0 && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">💡 Recomendaciones</h5>
                                                        <ul className="hipopotamo-lista">
                                                            {reporteIA.recomendaciones.map((rec, i) => (
                                                                <li key={i} className="rinoceronte-item rinoceronte-exito">
                                                                    {rec}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {reporteIA.acciones_inmediatas && reporteIA.acciones_inmediatas.length > 0 && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">🎯 Acciones Inmediatas</h5>
                                                        <ul className="hipopotamo-lista">
                                                            {reporteIA.acciones_inmediatas.map((accion, i) => (
                                                                <li key={i} className="rinoceronte-item rinoceronte-advertencia">
                                                                    {accion}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {reporteIA.pronostico && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">🔮 Pronóstico</h5>
                                                        <p>{reporteIA.pronostico}</p>
                                                    </div>
                                                )}

                                                {reporteIA.optimizaciones && reporteIA.optimizaciones.length > 0 && (
                                                    <div className="serpiente-seccion">
                                                        <h5 className="tortuga-subtitulo">⚡ Optimizaciones</h5>
                                                        <ul className="hipopotamo-lista">
                                                            {reporteIA.optimizaciones.map((opt, i) => (
                                                                <li key={i} className="rinoceronte-item rinoceronte-exito">
                                                                    {opt}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
}

export default Analicis;